import { AuthAdaptorService, AuthService, EncryptedContactIdRouteInterceptor } from '@aiwyn/auth';
import { ConfigService } from '@aiwyn/config';
import { EngagementAvaliabilityCheck } from '@aiwyn/engagements';
import { LoadableHelper, Logger } from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ROUTE_TOKEN_ENGAGEMENTS_BASE, ROUTE_TOKEN_MAIN_BASE } from '../routing-constants';
import { AppState } from '../state';

export const ROUTE_DATA_TOKEN_ENGAGEMENTS_IS_DETAIL_VIEW: string = 'isDetailView';

@Injectable({ providedIn: 'root' })
export class EngagementsGuard  {
  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly configService: ConfigService,
    private readonly authAdaptor: AuthAdaptorService,
    private readonly authService: AuthService,
    private readonly logger: Logger,
    private readonly encryptedContactId: EncryptedContactIdRouteInterceptor,
  ) { }

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean {
    // Access navigation state from Router.getCurrentNavigation()
    const navigation = this.router.getCurrentNavigation();
    const navigationState = navigation?.extras?.state;

    return this.isEngagementsEnabled(route, navigationState);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | boolean {
    // Access navigation state from Router.getCurrentNavigation()
    const navigation = this.router.getCurrentNavigation();
    const navigationState = navigation?.extras?.state;

    return this.isEngagementsEnabled(childRoute, navigationState);
  }

  isEngagementsVisible(engagementId: number, forceRefresh: boolean): Observable<boolean> {
    if (!this.configService.engagementsEnabled) {
      this.logger.info('AUTH :: EngagementsGuard :: isEngagementsVisible :: engagements are not enabled, no visibility.');
      return of(false);
    }

    return this.authService.getUserLoadable().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status)),
      take(1),
      switchMap(({ data: user }) => {
        if (user.isAnonymous()) {
          this.logger.info('AUTH :: EngagementsGuard :: isEngagementsVisible :: user is anonymous, user must be known to see engagements.');
          return of(false);
        }

        // note: removed encrypted contact id since the decryptedData is always undefined and we are checing it on the application user factory service and throwing the "ContactUserIdMismatchError" exception

        this.store.dispatch(new EngagementAvaliabilityCheck({ engagementId, forceRefresh }));

        return this.store.select(AppState.getShowEngagementsTab).pipe(
          filter(showEngagementsTab => showEngagementsTab !== undefined),
          map(showEngagementsTab => !!showEngagementsTab),
        );
      }),
    );
  }

  isEngagementsEnabled(route: ActivatedRouteSnapshot, navigationState?: any): Observable<boolean | UrlTree> | boolean {
    this.logger.info('AUTH :: EngagementsGuard :: isEngagementsEnabled :: starting engagements enabled check.');

    const engagementId = route.params?.['engagementId'] && !isNaN(route.params?.['engagementId'])
      ? Number(route.params?.['engagementId'])
      : undefined;

    return this.isEngagementsVisible(engagementId, !!navigationState?.forceRefresh).pipe(
      map(hasEngagementsVisibility => {
        if (!hasEngagementsVisibility) {
          if (engagementId) {
            this.logger.info('AUTH :: EngagementsGuard :: isEngagementsEnabled :: engagements are not visible, redirecting to engagements base.');
            return this.router.createUrlTree([ ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_ENGAGEMENTS_BASE]);
          }


          this.logger.info('AUTH :: EngagementsGuard :: isEngagementsEnabled :: engagements are not visible, redirecting to main base.');
          return this.router.createUrlTree([ROUTE_TOKEN_MAIN_BASE]);
        }

        return true;
      }),
    );
  }
}
